import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { FixtureRow } from '@/components/matches/FixtureRow';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';
import { useStandings } from '@/features/matches/hooks';
import type { CompetitionRef, FixtureWithRefs } from '@/lib/data/provider';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeCompetition, localizeRound } from '@/lib/i18n/footballNames';

export interface FixtureGroup {
  key: string;
  competition: CompetitionRef | null;
  fixtures: FixtureWithRefs[];
  hasLive: boolean;
}

/** Live first, then upcoming (soonest next), finished sinks to the bottom. */
const STATUS_RANK: Record<string, number> = { live: 0, scheduled: 1, postponed: 2, finished: 3 };
function orderFixtures(fixtures: FixtureWithRefs[]): FixtureWithRefs[] {
  return [...fixtures].sort((a, b) => {
    const rank = (STATUS_RANK[a.status] ?? 1) - (STATUS_RANK[b.status] ?? 1);
    if (rank !== 0) return rank;
    return new Date(a.kickoff_at).getTime() - new Date(b.kickoff_at).getTime();
  });
}

/** A small "GROUP C" style sub-header that splits a competition's day by group. */
function GroupLabel({ label }: { label: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const letter = label.replace(/group\s*/i, '').trim();
  const text = locale === 'ar' ? localizeRound(label, locale) : `${t('matches.group')} ${letter}`;
  return (
    <View className="mx-4 mb-2 mt-1 self-start rounded-full bg-surface-2/70 px-3 py-1">
      <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-soft">
        {text}
      </AppText>
    </View>
  );
}

/**
 * 365Scores/FotMob-style league section: a glassy header (crest · name · country)
 * that deep-links into the competition, plus a collapse chevron. For group-stage
 * competitions (World Cup) the day's fixtures are further split into "GROUP A/B…"
 * sub-sections, mapped from the standings' group_label.
 */
function CompetitionGroupImpl({
  group,
  followedTeamIds,
}: {
  group: FixtureGroup;
  /** Teams the user follows / favourites - their fixtures get a heart frame. */
  followedTeamIds?: Set<string>;
}) {
  const palette = useThemePalette();
  const router = useRouter();
  const { locale } = useLocale();
  const isFollowed = (fixture: FixtureWithRefs): boolean =>
    !!followedTeamIds &&
    ((fixture.home_team_id != null && followedTeamIds.has(fixture.home_team_id)) ||
      (fixture.away_team_id != null && followedTeamIds.has(fixture.away_team_id)));
  const [collapsed, setCollapsed] = useState(false);
  const comp = group.competition;
  const name = localizeCompetition(comp?.name, locale) || '-';
  const season = group.fixtures[0]?.season;
  // Only group-staged competitions (the World Cup) need standings to derive the
  // group_label split; domestic leagues never use it, so skip the ~28 KB fetch
  // for them (this fires once per competition group on the home feed).
  const hasGroupRounds = useMemo(
    () => group.fixtures.some((f) => !!f.round && /group/i.test(f.round)),
    [group.fixtures],
  );
  const standings = useStandings(hasGroupRounds ? comp?.id : undefined, season);

  // team_id → "Group A". Empty for non-group-staged competitions.
  const teamGroup = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of standings.data ?? []) {
      if (row.group_label) map.set(row.team_id, row.group_label);
    }
    return map;
  }, [standings.data]);

  // Split the day into ordered [groupLabel, fixtures] buckets, or null when the
  // competition has no group staging (render the flat list as before). Only
  // GROUP-STAGE fixtures get a group header - knockout fixtures pair teams from
  // two different groups, so labelling them by the home team's group would be
  // wrong; they fall into the unlabelled bucket and render headerless.
  const subGroups = useMemo(() => {
    if (teamGroup.size === 0) return null;
    const isGroupStage = (round: string | null) => !!round && /group/i.test(round);
    const byGroup = new Map<string, FixtureWithRefs[]>();
    let anyLabelled = false;
    for (const fixture of group.fixtures) {
      const label = isGroupStage(fixture.round)
        ? (teamGroup.get(fixture.home_team_id ?? '') ??
          teamGroup.get(fixture.away_team_id ?? '') ??
          null)
        : null;
      if (label) anyLabelled = true;
      const key = label ?? '';
      const list = byGroup.get(key);
      if (list) list.push(fixture);
      else byGroup.set(key, [fixture]);
    }
    if (!anyLabelled) return null;
    // Groups with a live or upcoming match rise above fully-finished groups; the
    // label order (A, B, C...) is kept within each tier.
    const isActive = (fixtures: FixtureWithRefs[]) =>
      fixtures.some((f) => f.status === 'live' || f.status === 'scheduled');
    return [...byGroup.entries()].sort((a, b) => {
      const active = Number(isActive(b[1])) - Number(isActive(a[1]));
      if (active !== 0) return active;
      return a[0].localeCompare(b[0], undefined, { numeric: true });
    });
  }, [group.fixtures, teamGroup]);

  return (
    <View className="mb-3.5">
      <View className="relative mx-4 mb-2 overflow-hidden rounded-card border border-line">
        {/* Glass sheen: translucent base + top highlight gradient */}
        <View className="absolute inset-0 bg-surface/80" />
        <LinearGradient
          colors={['rgba(255,255,255,0.06)', 'rgba(255,255,255,0)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        />
        <View className="flex-row items-center gap-3 px-4 py-3.5">
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={name}
            disabled={!comp}
            onPress={() => comp && router.push(`/competition/${comp.id}`)}
            className="flex-1 flex-row items-center gap-2.5 active:opacity-80"
          >
            <TeamCrest uri={comp?.logo_url} name={name} size="sm" />
            <View className="flex-1">
              <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
                {name}
              </AppText>
              {comp?.country_code ? (
                <AppText
                  variant="micro"
                  weight="medium"
                  className="uppercase tracking-wide text-muted"
                  numberOfLines={1}
                >
                  {comp.country_code}
                </AppText>
              ) : null}
            </View>
          </Pressable>
          {group.hasLive ? <View className="h-1.5 w-1.5 rounded-full bg-volt" /> : null}
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: !collapsed }}
            onPress={() => setCollapsed((value) => !value)}
            hitSlop={8}
            className="px-1 py-1 active:opacity-70"
          >
            <Ionicons name={collapsed ? 'chevron-down' : 'chevron-up'} size={16} color={palette.soft} />
          </Pressable>
        </View>
      </View>
      {collapsed ? null : subGroups ? (
        subGroups.map(([label, fixtures]) => (
          <View key={label || 'none'}>
            {label ? <GroupLabel label={label} /> : null}
            {orderFixtures(fixtures).map((fixture) => (
              <FixtureRow
                key={fixture.id}
                fixture={fixture}
                hideCompetition
                followed={isFollowed(fixture)}
              />
            ))}
          </View>
        ))
      ) : (
        orderFixtures(group.fixtures).map((fixture) => (
          <FixtureRow
            key={fixture.id}
            fixture={fixture}
            hideCompetition
            followed={isFollowed(fixture)}
          />
        ))
      )}
    </View>
  );
}

/** Memoized so unaffected competition groups skip re-render on a live tick. */
export const CompetitionGroup = React.memo(CompetitionGroupImpl);
